import { interpolateColor } from 'common/Utils/RGBUtils'
import Breadcrumbs from 'components/Common/Breadcrumb'
import Loader from 'components/Common/Loader'
import Switch from 'components/Common/Switch/Switch'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MetaTags } from 'react-meta-tags'
import { connect } from 'react-redux'
import { getSalesFunnelData } from 'store/actions'

/**
 * @param {{
*   salesFunnelData: {
*     averages: Array<{
*       interactionType: string,
*       interactionNumber: number,
*       averageMinutesSpentOnStep: number,
*       numberOfUsersThatHaveReachedThisStep: number,
*       percentageOfUsersThatHaveReachedThisStep: number,
*       numberOfusersThatHaveStoppedInThisStep: number,
*       percentageOfusersThatHaveStoppedInThisStep: number,
*     }>
*   }
* }} props
 */
export const SalesFunnel = (props) => {
	const { t } = useTranslation('naming')
	const { salesFunnelData, getSalesFunnelData, getSalesFunnelDataStatus } = props
	const [filterOutIsLikelyReturningCustomer, setFilterOutIsLikelyReturningCustomer] = useState(true)

	useEffect(() => {
		if (!getSalesFunnelDataStatus?.isLoading && salesFunnelData == null) {
			getSalesFunnelData()
		}
	}, [])

	const salesFunnelDataMapped = useMemo(() => {
		if (salesFunnelData == null) return null

		const mapAverages = (averages) => {

			const interactionTypesLength = averages.length

			const getColorByInteractionNumber = (interactionNumber, opacity = 0.25) => {
				return interpolateColor({
					orderOfImportance: interactionNumber,
					maxOrderOfImportance: interactionTypesLength,
					colorStops: [
						{ t: 0, rgba: [255, 0, 0, opacity] }, // Red
						{ t: 0.33, rgba: [255, 165, 0, opacity] }, // Orange
						{ t: 0.66, rgba: [255, 255, 0, opacity] }, // Yellow
						{ t: 1, rgba: [0, 255, 0, opacity] }, // Green
					],
				})
			}

			const getIconByInteractionType = (interactionType) => ({
				'static': 'bxl-wordpress',
				'app_no_auth': 'bx-lock',
				'new_offer_valid': 'bxs-pyramid',
				'new_offer_auth_modal': 'bx-windows',
				'logged_in': 'bx-log-in',
				'signed_up': 'bx-user-plus',
				'email_validated': 'bx-envelope',
				'new_offer_submit': 'bx-send',
				'signup_completed': 'bx-user-check',
				'viewed_quote': 'bx-show',
				'added_to_cart': 'bx-cart',
				'visited_checkout_1': 'bx-credit-card',
				'visited_checkout_2': 'bx-credit-card',
				'visited_checkout_3': 'bx-credit-card',
				'order_purchased': 'bx-check-circle',
			}[interactionType])

			return averages.map((averagesItem) => {
				const minutes = Math.floor(averagesItem.averageMinutesSpentOnStep) || '0'
				const hours = averagesItem.averageMinutesSpentOnStep >= 60 ? Math.floor(averagesItem.averageMinutesSpentOnStep / 60) : undefined
				const days = averagesItem.averageMinutesSpentOnStep >= 1440 ? Math.floor(averagesItem.averageMinutesSpentOnStep / 1440) : undefined
				const timeUsed = days || hours || minutes
				const timeUnitUsed = days ? 'days' : hours ? 'hours' : 'minutes'
				return {
					interactionType: averagesItem.interactionType,
					interactionNumber: averagesItem.interactionNumber,
					backgroundColor: getColorByInteractionNumber(averagesItem.interactionNumber),
					icon: getIconByInteractionType(averagesItem.interactionType),
					averageTimeSpentOnStep: `${timeUsed} ${t(timeUnitUsed)}`,
					usersThatHaveReachedThisStep: {
						quantity: averagesItem.numberOfUsersThatHaveReachedThisStep,
						percentage: averagesItem.percentageOfUsersThatHaveReachedThisStep,
						backgroundColor: interpolateColor({
							orderOfImportance: averagesItem.percentageOfUsersThatHaveReachedThisStep,
							maxOrderOfImportance: 100,
							colorStops: [
								{ t: 0, rgba: [255, 0, 0, 0.2] }, // Red
								{ t: 1, rgba: [0, 123, 255, 0.2] }, // Light blue
							],
						}),
					},
					usersThatHaveStoppedAtThisStep: {
						quantity: averagesItem.numberOfusersThatHaveStoppedInThisStep,
						percentage: averagesItem.percentageOfusersThatHaveStoppedInThisStep,
						backgroundColor: interpolateColor({
							orderOfImportance: averagesItem.percentageOfusersThatHaveStoppedInThisStep,
							maxOrderOfImportance: 100,
							colorStops: [
								{ t: 0, rgba: [255, 0, 0, 0.2] }, // Red
								{ t: 1, rgba: [0, 123, 255, 0.2] }, // Light blue
							],
						}),
					},
				}
			})
		}

		return {
			averagesWithLikelyReturningUsers: mapAverages(salesFunnelData.averagesWithLikelyReturningUsers),
			averagesWithoutLikelyReturningUsers: mapAverages(salesFunnelData.averagesWithoutLikelyReturningUsers),
		}

	}, [salesFunnelData])

	return (
		<div className="page-content mb-4">
			<MetaTags>
				<title>Proto&Go! | {t('salesFunnel')}</title>
			</MetaTags>
			<Breadcrumbs
				title={t('management')}
				breadcrumbItems={[
					{
						item: t('salesFunnel'),
						link: '/salesFunnel',
					},
				]}
			/>
			{/* --------------------------------- Page Content --------------------------------- */}
			{salesFunnelData == null ? (
				/* --------------------------------- Loading --------------------------------- */
				<Loader className="font-size-80 mx-auto" />
			) : getSalesFunnelDataStatus?.error ? (
				/* --------------------------------- Error --------------------------------- */
				<div>{t('error_message')}</div>
			) : (
				/* --------------------------------- Content --------------------------------- */
				<div className="bg-white my-4 p-4 marginFooter">
					{/* --------------------------------- switchwtcih --------------------------------- */}
					<Switch
						label={t('include_likely_returning_users')}
						value={!filterOutIsLikelyReturningCustomer}
						onChange={() => setFilterOutIsLikelyReturningCustomer(!filterOutIsLikelyReturningCustomer)}
						size="md"
						className="mt-3 mb-3"
					/>
					{/* --------------------------------- Table Averages --------------------------------- */}
					<table className="table-no-bg py-2">
						{/* --------------------------------- Table Averages - Header Row --------------------------------- */}
						<thead>
							<tr style={{ backgroundColor: '#f8f9fa' }}>
								<th>{t('interaction_type')}</th>
								<th>{t('average_minutes_spent_on_step')}</th>
								<th>{t('users_that_have_reached_this_step')}</th>
								<th>{t('users_that_have_stopped_in_this_step')}</th>
							</tr>
						</thead>
						{/* --------------------------------- Table Averages - Data Rows --------------------------------- */}
						<tbody>
							{(filterOutIsLikelyReturningCustomer
								? salesFunnelDataMapped.averagesWithoutLikelyReturningUsers
								: salesFunnelDataMapped.averagesWithLikelyReturningUsers).map((averagesItem, index) => {
									const rowBgColor = index % 2 === 0 ? 'white' : '#f8f9fa'
									return (
										<tr
											key={`intTypeAvg${index}`}
											style={{ cursor: 'pointer', backgroundColor: rowBgColor, borderTop: 'solid 2px #eff2f7' }}
										>
											{/* --------------------------------- Table Averages - Column 1 - Interaction Type --------------------------------- */}
											<td className="align-middle">
												<span
													className="d-inline-flex align-items-center gap-1 p-1 px-2 rounded"
													style={{ backgroundColor: averagesItem.backgroundColor }}
												>
													{'#'}{averagesItem.interactionNumber} - <i className={`bx ${averagesItem.icon}`}></i>
													{t(averagesItem.interactionType)}
												</span>
											</td>

											{/* --------------------------------- Table Averages - Column 2 - Average minutes spent on step --------------------------------- */}
											<td className="align-middle">
												{averagesItem.averageTimeSpentOnStep}
											</td>

											{/* --------------------------------- Table Averages - Column 3 - Users that have reached this step (quantity & percentage) --------------------------------- */}
											<td
												className="align-middle"
												style={{ position: 'relative' }}
											>
												<div
													style={{
														position: 'absolute',
														top: 0,
														left: 0,
														height: '100%',
														width: `${averagesItem.usersThatHaveReachedThisStep.percentage}%`,
														backgroundColor: averagesItem.usersThatHaveReachedThisStep.backgroundColor,
													}}
												/>
												<span style={{ position: 'relative', zIndex: 1 }}>
													{`${averagesItem.usersThatHaveReachedThisStep.quantity} (${averagesItem.usersThatHaveReachedThisStep.percentage}%)`}
												</span>
											</td>
											{/* --------------------------------- Table Averages - Column 3 - Users that have stopped on this step (quantity & percentage) --------------------------------- */}
											<td
												className="align-middle"
												style={{ position: 'relative' }}
											>
												<div
													style={{
														position: 'absolute',
														top: 0,
														left: 0,
														height: '100%',
														width: `${averagesItem.usersThatHaveStoppedAtThisStep.percentage}%`,
														backgroundColor: averagesItem.usersThatHaveStoppedAtThisStep.backgroundColor,
													}}
												/>
												<span style={{ position: 'relative', zIndex: 1 }}>
													{`${averagesItem.usersThatHaveStoppedAtThisStep.quantity} (${averagesItem.usersThatHaveStoppedAtThisStep.percentage}%)`}
												</span>
											</td>
										</tr>
									)
								})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

SalesFunnel.propTypes = {
	salesFunnelData: PropTypes.shape({
		averagesWithLikelyReturningUsers: PropTypes.arrayOf(
			PropTypes.shape({
				interactionType: PropTypes.string.isRequired,
				interactionNumber: PropTypes.number.isRequired,
				averageMinutesSpentOnStep: PropTypes.number.isRequired,
				numberOfUsersThatHaveReachedThisStep: PropTypes.number.isRequired,
				percentageOfUsersThatHaveReachedThisStep: PropTypes.number.isRequired,
				numberOfusersThatHaveStoppedInThisStep: PropTypes.number.isRequired,
				percentageOfusersThatHaveStoppedInThisStep: PropTypes.number.isRequired,
			}).isRequired,
		).isRequired,
		averagesWithoutLikelyReturningUsers: PropTypes.arrayOf(
			PropTypes.shape({
				interactionType: PropTypes.string.isRequired,
				interactionNumber: PropTypes.number.isRequired,
				averageMinutesSpentOnStep: PropTypes.number.isRequired,
				numberOfUsersThatHaveReachedThisStep: PropTypes.number.isRequired,
				percentageOfUsersThatHaveReachedThisStep: PropTypes.number.isRequired,
				numberOfusersThatHaveStoppedInThisStep: PropTypes.number.isRequired,
				percentageOfusersThatHaveStoppedInThisStep: PropTypes.number.isRequired,
			}).isRequired,
		).isRequired,
	}),
	getSalesFunnelData: PropTypes.func,
	getSalesFunnelDataStatus: PropTypes.shape({
		isLoading: PropTypes.bool,
		success: PropTypes.bool,
		error: PropTypes.string,
	}),
}

const mapStateToProps = (state) => ({
	salesFunnelData: state.Users.salesFunnelData,
	getSalesFunnelData: state.Users.getSalesFunnelData,
})

const mapDispatchToProps = { getSalesFunnelData }

export default connect(mapStateToProps, mapDispatchToProps)(SalesFunnel)
